/**
 * â­ CorporateStandardsSection â€” Excelência Module
 *
 * Bloco destacado visualmente (background diferenciado)
 * exibindo apenas blueprints marcados como padrão corporativo.
 *
 * Filtro estrito: isCorporateStandard = true AND status = 'standard'
 *
 * Exibe por item: Versão Â· Projetos utilizando Â· Data de publicação
 */

import React, { useMemo } from 'react';
import {
  Award,
  Star,
  Copy,
  Loader2,
  Shield,
  Users,
  Calendar,
  Tag,
} from 'lucide-react';
import type { ProcessBlueprint, BlueprintUsageCount } from '../../../types/processBlueprint';
import { CATEGORY_LABELS } from '../../../types/processBlueprint';

interface CorporateStandardsSectionProps {
  blueprints: ProcessBlueprint[];
  processingId: string | null;
  usageCounts?: BlueprintUsageCount[];
  /** Se true, exibe ação de remover padrão corporativo */
  canGovernance?: boolean;
  onToggleStandard: (blueprint: ProcessBlueprint) => void;
  onClone: (blueprint: ProcessBlueprint) => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const CorporateStandardsSection: React.FC<CorporateStandardsSectionProps> = React.memo(
  ({ blueprints, processingId, usageCounts, canGovernance = false, onToggleStandard, onClone }) => {
    // Filtro estrito: ambos devem ser verdadeiros
    const standards = useMemo(
      () => blueprints.filter(b => b.isCorporateStandard === true && b.status === 'standard'),
      [blueprints],
    );

    // Lookup rápido de uso por blueprint
    const usageMap = useMemo(() => {
      const lookup: Record<string, number> = {};
      if (usageCounts) {
        usageCounts.forEach(u => { lookup[u.blueprintId] = u.usageCount; });
      }
      return lookup;
    }, [usageCounts]);

    return (
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-gray-800 dark:text-slate-200">
              Padrões Corporativos
            </h2>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
              {standards.length}
            </span>
          </div>
          <span className="text-xs text-gray-500 dark:text-slate-400">
            Blueprints de referência para a organização
          </span>
        </div>

        <div className="bg-gradient-to-br from-purple-50/60 via-white to-purple-50/30 dark:from-purple-950/20 dark:via-neutral-900 dark:to-purple-950/10 border border-purple-200/60 dark:border-purple-900/40 rounded overflow-hidden">
          {standards.length === 0 ? (
            /* â”€â”€ Empty state â”€â”€ */
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-4">
                <Star size={28} className="text-purple-500 dark:text-purple-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-200 mb-1">
                Nenhum padrão corporativo definido
              </h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 text-center max-w-md mb-4">
                Publique um blueprint e marque-o como padrão corporativo para criar referências
                reutilizáveis em toda a organização.
              </p>
              <div className="flex items-center gap-2 text-[11px] text-purple-600 dark:text-purple-400 font-medium">
                <Shield size={14} />
                <span>Padrões corporativos garantem consistência entre projetos</span>
              </div>
            </div>
          ) : (
            /* â”€â”€ Grid de padrões â”€â”€ */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-purple-200/30 dark:bg-purple-900/20">
              {standards.map(bp => {
                const isProcessing = processingId === bp.id;
                const usage = usageMap[bp.id] ?? 0;

                return (
                  <div
                    key={bp.id}
                    className="bg-white/90 dark:bg-[var(--bg-surface)] p-5 hover:bg-purple-50/50 dark:hover:bg-purple-950/20 transition-colors"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                          <Award size={16} className="text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate">
                            {bp.name}
                          </h4>
                          {bp.category && bp.category !== 'geral' && (
                            <span className="text-[9px] px-1.5 py-0.5 mt-0.5 inline-flex items-center gap-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-sm">
                              <Tag size={8} />
                              {CATEGORY_LABELS[bp.category] ?? bp.category}
                            </span>
                          )}
                        </div>
                      </div>
                      <Shield size={14} className="text-purple-500 dark:text-purple-400 flex-shrink-0 mt-1" />
                    </div>

                    {/* Info: Versão Â· Em Uso Â· Publicação */}
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                      <span className="text-[11px] font-mono font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                        v{bp.versionSemver}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-slate-400">
                        <Users size={11} className="text-blue-500" />
                        {usage > 0
                          ? `${usage} projeto${usage !== 1 ? 's' : ''}`
                          : 'Sem uso'}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-slate-500">
                        <Calendar size={11} />
                        {formatDate(bp.updatedAt)}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        disabled={isProcessing}
                        onClick={() => onClone(bp)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100/80 dark:bg-purple-900/30 hover:bg-purple-200/80 dark:hover:bg-purple-900/50 rounded-md transition-colors disabled:opacity-50"
                      >
                        {isProcessing ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Copy size={12} />
                        )}
                        Usar como base
                      </button>
                      {canGovernance && (
                        <button
                          disabled={isProcessing}
                          onClick={() => onToggleStandard(bp)}
                          className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-md transition-colors disabled:opacity-50"
                          title="Remover padrão corporativo"
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    );
  },
);

CorporateStandardsSection.displayName = 'CorporateStandardsSection';
